import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Users, TrendingUp, CheckCircle, Code, Car, Search } from 'lucide-react';

export const AutoDealerPortfolio: React.FC = () => {
  const technologies = [
    'React', 'Node.js', 'MySQL', 'AJAX', 'Stripe API', 'Third-party APIs'
  ];

  const features = [
    'Vehicle Inventory Management',
    'Advanced Search & Filters',
    'Financing Calculator',
    'Trade-in Valuation',
    'Service Scheduling',
    'Customer Portal',
    'Lead Management',
    'Mobile Responsive'
  ];

  const results = [
    { metric: 'Online Leads', value: '220%', description: 'Increase in qualified leads' },
    { metric: 'Inventory Views', value: '85%', description: 'Improvement in vehicle page views' },
    { metric: 'Service Bookings', value: '70%', description: 'Of appointments now online' },
    { metric: 'Customer Satisfaction', value: '94%', description: 'Positive feedback rating' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/portfolio" className="inline-flex items-center text-primary-100 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Portfolio
          </Link>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Auto Dealer
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Premier Auto Group
              </h1>
              <p className="text-xl text-primary-100 mb-8 leading-relaxed">
                A comprehensive automotive dealership platform with advanced inventory management, 
                financing tools, and service scheduling that increased online leads by 220% and improved customer satisfaction to 94%.
              </p>
            </div>
            
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
                alt="Auto Dealership"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                <p className="mb-6">
                  Premier Auto Group needed a modern digital platform to showcase their extensive 
                  vehicle inventory, streamline the car buying process, and provide comprehensive 
                  service scheduling for their customers across multiple locations.
                </p>
                <p className="mb-6">
                  We developed a sophisticated React-based platform with real-time inventory management, 
                  advanced search capabilities, integrated financing calculators, and comprehensive 
                  customer portal. The system integrates with their existing DMS and third-party 
                  automotive data providers.
                </p>
                <p>
                  The implementation resulted in 220% increase in qualified leads, 85% improvement 
                  in vehicle page views, and 70% of service appointments now booked online.
                </p>
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-6 w-6 text-primary-600 mr-2" />
                  Project Timeline
                </h3>
                <div className="space-y-3 text-gray-600">
                  <div className="flex justify-between">
                    <span>Duration:</span>
                    <span className="font-medium">16 weeks</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Launch Date:</span>
                    <span className="font-medium">August 2023</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Locations:</span>
                    <span className="font-medium">5 dealerships</span>
                  </div>
                </div>
              </div>
              
              <div className="card p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Code className="h-6 w-6 text-primary-600 mr-2" />
                  Technologies Used
                </h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, index) => (
                    <span key={index} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features Delivered</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive automotive dealership features designed to enhance the car buying and service experience.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="card p-6 text-center">
                <CheckCircle className="h-8 w-8 text-accent-500 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-900">{feature}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results & Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Results & Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Exceptional growth in lead generation and customer engagement across all dealership locations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-primary-600 mb-2">{result.value}</div>
                <div className="text-lg font-semibold text-gray-900 mb-2">{result.metric}</div>
                <div className="text-gray-600">{result.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Challenges & Solutions</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Car className="h-8 w-8 text-blue-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Real-time Inventory</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Synchronizing vehicle inventory across multiple locations and sales channels while 
                maintaining accurate availability and pricing information.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Implemented real-time API integration with their DMS system 
                and automated inventory updates to ensure accurate, up-to-the-minute vehicle information.
              </p>
            </div>
            
            <div className="card p-8">
              <div className="flex items-center mb-6">
                <Search className="h-8 w-8 text-green-500 mr-3" />
                <h3 className="text-xl font-semibold text-gray-900">Challenge: Advanced Search</h3>
              </div>
              <p className="text-gray-600 mb-4">
                Creating an intuitive search experience that could handle complex filtering across 
                thousands of vehicles with multiple attributes and specifications.
              </p>
              <p className="text-gray-600">
                <strong>Solution:</strong> Developed an intelligent search system with AJAX-powered 
                filtering, auto-suggestions, and saved search capabilities for optimal user experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonial */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <blockquote className="text-2xl font-medium mb-8">
            "The new platform has completely transformed our business. We're generating more qualified 
            leads than ever before, and our customers love the seamless online experience."
          </blockquote>
          <div className="flex items-center justify-center">
            <img
              src="https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&fit=crop"
              alt="Robert Martinez"
              className="w-16 h-16 rounded-full mr-4"
            />
            <div className="text-left">
              <div className="font-semibold">Robert Martinez</div>
              <div className="text-primary-200">General Manager, Premier Auto Group</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Accelerate Your Auto Business?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how we can create a comprehensive automotive platform that drives sales and customer satisfaction.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-primary text-lg px-8 py-4">
              Start Your Project
            </Link>
            <Link to="/portfolio" className="btn-outline text-lg px-8 py-4">
              View More Projects
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};